import type { FC, ReactNode } from "react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import { useTooltipContainerPosition } from "@ui/hooks/useTooltipContainerPosition";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { UiCrossIcon } from "@jti/brand-icons/ui-cross";

import { DEPRECATED_IconButton } from "../DEPRECATED_IconButton";

import { InfoIcon } from "./InfoIcon/InfoIcon";

import styles from "./Tooltip.module.scss";

type TooltipProps = {
  size?: "small" | "large";
  withCloseIcon?: boolean;
  content?: ReactNode;
  dataTestId?: string;
  iconClassName?: string;
  IconSlot?: React.ElementType;
  behaviour?: "click" | "hover";
  onToggle?: (event: React.MouseEvent<HTMLDivElement>, isOpen: boolean) => void;
  wrapperClassName?: string;
};

export const Tooltip: FC<TooltipProps> = ({
  content,
  size = "small",
  withCloseIcon,
  dataTestId,
  iconClassName,
  IconSlot,
  behaviour = "click",
  onToggle,
  wrapperClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const { __ } = useTranslations();

  useOutsideClick(containerRef, () => setIsOpen(false), { excludeRefs: [iconRef] });
  useTooltipContainerPosition(iconRef, containerRef, isOpen, [mounted]);

  useEffect(() => {
    if (!isOpen) {
      setMounted(false);
      return;
    }

    setMounted(true);
  }, [isOpen]);

  const handleOpenByKeyboard = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.code === "Enter" || event.code === "Space") {
      setIsOpen((prev) => {
        if (prev) {
          iconRef.current?.focus();
        }
        return !prev;
      });
    }
  }, []);

  const handleToggle: React.MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();

    setIsOpen((prev) => {
      if (prev) {
        iconRef.current?.focus();
      }

      return !prev;
    });
    if (onToggle) {
      onToggle(event, !isOpen);
    }
  };

  return (
    <div className={classNames(wrapperClassName || styles.wrapper)} data-testid={dataTestId}>
      <div
        ref={iconRef}
        className={classNames(styles.icon, iconClassName)}
        onKeyDown={handleOpenByKeyboard}
        data-testid="tooltip-icon"
        onClick={behaviour === "click" ? handleToggle : undefined}
        onMouseEnter={behaviour === "hover" ? () => setIsOpen(true) : undefined}
        onMouseLeave={behaviour === "hover" ? () => setIsOpen(false) : undefined}
        tabIndex={0}
        data-size={size}
        style={{
          "--tooltip-offset":
            size === "small"
              ? "var(--des-dimension-gap-and-padding-general-away-tiny)"
              : "var(--des-dimension-gap-and-padding-general-away-default)",
        }}
      >
        {IconSlot && <IconSlot />}
        {!IconSlot && <InfoIcon isOpen={isOpen} size={size} />}
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={(node) => {
              containerRef.current = node;

              if (node) {
                node.focus();
              }
            }}
            tabIndex={0}
            className={classNames(styles.window, { [styles.open]: isOpen && mounted })}
            data-testid="tooltip-window"
            onKeyDown={handleOpenByKeyboard}
            role="tooltip"
          >
            {withCloseIcon && (
              <DEPRECATED_IconButton
                onClick={() => setIsOpen(false)}
                className={classNames(styles.closeButton, "dark-theme")}
                iconPosition="end"
                icon={UiCrossIcon}
                aria-label={__("common.close")}
              />
            )}
            <div className={styles.text}>{content}</div>
          </div>,
          document.body
        )}
    </div>
  );
};
